import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Stack,
  IconButton,
  Typography,
  CssBaseline,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Tooltip
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import GameLogModal from '../components/GameLogModal';
import { useStoreData } from '../useStoreData';
import { useLogoMaps } from '../useLogoMaps';
import ProjectionsTutorial from './ProjectionsTutorial';

// Custom dark theme matching Pickem
const customTheme = createTheme({
  palette: {
    primary: { main: '#ff1744', contrastText: '#ffffff' },
    background: { default: '#121212', paper: '#1e1e1e' },
  },
  typography: { fontFamily: 'Roboto, Arial, sans-serif', h5: { fontWeight: 600 } },
  components: {
    MuiInputLabel: { styleOverrides: { root: { color: '#ffffff' } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          backgroundColor: '#333333',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
        },
      },
    },
    MuiMenu: { styleOverrides: { paper: { backgroundColor: '#333333', color: '#ffffff' } } },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          '&.Mui-selected': { backgroundColor: '#444444' },
          '&.Mui-selected:hover': { backgroundColor: '#555555' },
          '&:hover': { backgroundColor: '#444444' },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: { root: { color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } } },
    },
  },
});

const BASKETBALL_LEAGUES = new Set(['NBA', 'NCAAB', 'WNBA']);
const GAME_LOG_LEAGUES = new Set(['NBA']);  // leagues with game log API support

const Projections = ({ userName, isAuthenticated }) => {
  const allProjections = useStoreData('projections') || [];
  const leaguesConfig = useStoreData('leagues') || [];
  const { teamMap } = useLogoMaps();

  // Load league selection from localStorage, default to NBA
  const [selectedLeague, setSelectedLeague] = useState(() => {
    return localStorage.getItem('projections_selectedLeague') || 'NFL';
  });

  const [selectedPosition, setSelectedPosition] = useState(() => {
    return localStorage.getItem('projections_selectedPosition') || 'All';
  });
  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortModel, setSortModel] = useState([{ field: 'Points', sort: 'desc' }]); // Default to Points for NBA

  const [searchQuery, setSearchQuery] = useState('');

  // Game Log Modal State
  const [gameLogModalOpen, setGameLogModalOpen] = useState(false);
  const [selectedPlayerData, setSelectedPlayerData] = useState(null);
  const [gameLogData, setGameLogData] = useState(null);
  const [gameLogLoading, setGameLogLoading] = useState(false);

  // Save league selection to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('projections_selectedLeague', selectedLeague);
  }, [selectedLeague]);

  // Save position selection to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('projections_selectedPosition', selectedPosition);
  }, [selectedPosition]);

  const isBasketball = BASKETBALL_LEAGUES.has(selectedLeague);

  // Default sort per league/position tab: football sorts by the tab's primary
  // yardage stat (All = pass, then rush, then rec as tiebreak columns).
  // Uses selectedLeague directly so it has no ordering dependency.
  useEffect(() => {
    if (BASKETBALL_LEAGUES.has(selectedLeague)) {
      setSortModel([{ field: 'Points', sort: 'desc' }]);
      return;
    }
    const byTab = {
      QB: [{ field: 'PassingYards', sort: 'desc' }],
      RB: [{ field: 'RushingYards', sort: 'desc' }],
      WR: [{ field: 'ReceivingYards', sort: 'desc' }],
      TE: [{ field: 'ReceivingYards', sort: 'desc' }],
    };
    setSortModel(byTab[selectedPosition] || [
      { field: 'PassingYards', sort: 'desc' },
      { field: 'RushingYards', sort: 'desc' },
      { field: 'ReceivingYards', sort: 'desc' },
    ]);
  }, [selectedLeague, selectedPosition]);
  // Available leagues from config (only those that have projection data)
  const leagueOptions = useMemo(() => {
    // Get leagues that actually have projections
    const leaguesWithData = new Set(allProjections.map(p => p.league).filter(Boolean));
    if (leaguesConfig.length > 0) {
      const names = leaguesConfig.map(l => l.name || l.id).filter(Boolean);
      const merged = new Set([...names.filter(n => leaguesWithData.has(n)), ...leaguesWithData]);
      // If projections exist but none have league values yet, show config leagues
      if (merged.size === 0 && allProjections.length > 0) {
        return names.length > 0 ? names.sort() : ['NFL'];
      }
      return [...merged].sort();
    }
    return leaguesWithData.size > 0 ? [...leaguesWithData].sort() : ['NFL'];
  }, [leaguesConfig, allProjections]);

  // Reset league if saved value no longer available.
  // Guard: only after projections actually arrive — before data loads, leagueOptions
  // is a placeholder fallback, and resetting against it clobbered (and persisted
  // over) the user's saved league on every page load.
  useEffect(() => {
    if (allProjections.length > 0 && leagueOptions.length > 0 && !leagueOptions.includes(selectedLeague)) {
      setSelectedLeague(leagueOptions.includes('NFL') ? 'NFL' : leagueOptions[0]);
    }
  }, [allProjections.length, leagueOptions, selectedLeague]);

  // Position tabs - conditional based on sport type
  const positionTabs = useMemo(() => {
    if (isBasketball) {
      return ['All', 'G', 'F', 'C'];
    }
    // Football leagues
    return ['All', 'QB', 'RB', 'WR', 'TE'];
  }, [isBasketball]);

  // Reset position when league actually changes (ref guard: the effect also fires
  // on mount, which would wipe the restored localStorage position back to 'All')
  const prevLeagueRef = useRef(selectedLeague);
  useEffect(() => {
    if (prevLeagueRef.current !== selectedLeague) {
      prevLeagueRef.current = selectedLeague;
      setSelectedPosition('All');
      setCurrentPage(0);
    }
  }, [selectedLeague]);

  // Safety: if a restored position doesn't exist for this league's tab set
  // (e.g. saved 'QB' then switched to NBA on another device), fall back to All
  useEffect(() => {
    if (!positionTabs.includes(selectedPosition)) {
      setSelectedPosition('All');
    }
  }, [positionTabs, selectedPosition]);

  // Select active data source — filter unified store by league
  // Projections with empty/missing league pass through (backward compat)
  const activeProjections = useMemo(() => {
    return allProjections.filter(p => !p.league || p.league === selectedLeague);
  }, [allProjections, selectedLeague]);

  // Handle player name click to fetch game log
  const handlePlayerClick = useCallback(async (playerRow) => {
    // Only fetch game logs for supported leagues
    if (!GAME_LOG_LEAGUES.has(selectedLeague)) return;

    const personId = playerRow.PersonId;

    // Don't proceed if no PersonId
    if (!personId) {
      console.log('No PersonId for this player');
      return;
    }

    setGameLogLoading(true);

    try {
      // Determine the correct API endpoint based on league
      const league = selectedLeague.toLowerCase();
      const response = await fetch(`https://ws.betpros.ai:8080/api/player-game-logs/${league}/${personId}`);

      if (!response.ok) {
        console.error('Failed to fetch game log:', response.status);
        setGameLogLoading(false);
        return;
      }

      const data = await response.json();

      // Only open modal if we have game log data
      if (data && data.gameLogs && data.gameLogs.length > 0) {
        setSelectedPlayerData(playerRow);
        setGameLogData(data.gameLogs);
        setGameLogModalOpen(true);
      } else {
        console.log('No game log data available for this player');
      }
    } catch (error) {
      console.error('Error fetching game log:', error);
    } finally {
      setGameLogLoading(false);
    }
  }, [selectedLeague]);

  // Close modal handler
  const handleCloseModal = useCallback(() => {
    setGameLogModalOpen(false);
    setSelectedPlayerData(null);
    setGameLogData(null);
  }, []);

  // Tab change handler for position filter
  const handleTabChange = useCallback((event, newValue) => {
    setSelectedPosition(positionTabs[newValue]);
    setCurrentPage(0);
  }, [positionTabs]);

  // Shared columns (Position, Player, Event)
  const sharedColumns = useMemo(() => [
    {
      field: 'Position',
      headerName: 'Pos',
      flex: 0.5,
      minWidth: 60,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Pos
        </Typography>
      ),
      valueFormatter: (value) => value || '-',
    },
    {
      field: 'PlayerName',
      headerName: 'Player',
      flex: 2,
      minWidth: 180,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Player
        </Typography>
      ),
      renderCell: (params) => {
        if (!params?.row) return '-';
        const hasPersonId = params.row.PersonId;
        const isClickable = GAME_LOG_LEAGUES.has(selectedLeague) && hasPersonId;

        return (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            height: '100%',
            width: '100%'
          }}>
            {teamMap[params.row.teamId]?.logo && (
              <img
                src={teamMap[params.row.teamId]?.logo}
                alt=""
                style={{ width: 20, height: 20, objectFit: 'contain' }}
              />
            )}
            <Typography
              onClick={isClickable ? (e) => {
                e.stopPropagation();
                handlePlayerClick(params.row);
              } : undefined}
              sx={{
                fontSize: '0.875rem',
                cursor: isClickable ? 'pointer' : 'default',
                '&:hover': isClickable ? {
                  textDecoration: 'underline'
                } : {}
              }}
            >
              {params.value || '-'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'Event',
      headerName: 'Event',
      flex: 1.5,
      minWidth: 180,
      sortComparator: (v1, v2, param1, param2) => {
        const date1 = param1.api.getCellValue(param1.id, 'EventStart');
        const date2 = param2.api.getCellValue(param2.id, 'EventStart');

        if (!date1 && !date2) return 0;
        if (!date1) return 1;
        if (!date2) return -1;

        return new Date(date1).getTime() - new Date(date2).getTime();
      },
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Event
        </Typography>
      ),
      renderCell: (params) => {
        if (!params?.row) return '-';

        const away = teamMap[params.row.awayTeamId]?.abbreviation || '';
        const home = teamMap[params.row.homeTeamId]?.abbreviation || '';
        const eventStart = params.row.EventStart
          ? new Date(params.row.EventStart).toLocaleString('en-US', {
              year: '2-digit',
              month: 'numeric',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            }).replace(',', '')
          : '';

        return (
          <Box sx={{
            whiteSpace: 'pre-line',
            textAlign: 'left',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100%'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', paddingLeft: '8px' }}>
              {teamMap[params.row.awayTeamId]?.logo && (
                <img
                  src={teamMap[params.row.awayTeamId]?.logo}
                  alt={`${away} Logo`}
                  style={{ width: 'auto', height: '14px', paddingRight: '2px', objectFit: 'contain' }}
                  loading="lazy"
                />
              )}
              <Typography sx={{ fontSize: '0.875rem' }}>{away}</Typography>
              <Typography sx={{ fontSize: '0.75rem', mx: '2px' }}>@</Typography>
              {teamMap[params.row.homeTeamId]?.logo && (
                <img
                  src={teamMap[params.row.homeTeamId]?.logo}
                  alt={`${home} Logo`}
                  style={{ width: 'auto', height: '14px', paddingRight: '2px', objectFit: 'contain' }}
                  loading="lazy"
                />
              )}
              <Typography sx={{ fontSize: '0.875rem' }}>{home}</Typography>
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: '#aaaaaa', paddingLeft: '8px' }}>
              {eventStart}
            </Typography>
          </Box>
        );
      },
    },
  ], [selectedLeague, handlePlayerClick, teamMap]);

  // NBA-specific stat columns
  const nbaStatColumns = [
    {
      field: 'Points',
      headerName: 'Points',
      type: 'number',
      flex: 1,
      minWidth: 80,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Points
        </Typography>
      ),
      valueFormatter: (value) => value != null ? value.toFixed(2) : '-',
    },
    {
      field: 'ThreePointers',
      headerName: '3PT',
      type: 'number',
      flex: 1,
      minWidth: 80,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          3PT
        </Typography>
      ),
      valueFormatter: (value) => value != null ? value.toFixed(2) : '-',
    },
    {
      field: 'Rebounds',
      headerName: 'Rebounds',
      type: 'number',
      flex: 1,
      minWidth: 80,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Rebounds
        </Typography>
      ),
      valueFormatter: (value) => value != null ? value.toFixed(2) : '-',
    },
    {
      field: 'Assists',
      headerName: 'Assists',
      type: 'number',
      flex: 1,
      minWidth: 80,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Assists
        </Typography>
      ),
      valueFormatter: (value) => value != null ? value.toFixed(2) : '-',
    },
    {
      field: 'Turnovers',
      headerName: 'Turnovers',
      type: 'number',
      flex: 1,
      minWidth: 80,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Turnovers
        </Typography>
      ),
      valueFormatter: (value) => value != null ? value.toFixed(2) : '-',
    },
    {
      field: 'Steals',
      headerName: 'Steals',
      type: 'number',
      flex: 1,
      minWidth: 80,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Steals
        </Typography>
      ),
      valueFormatter: (value) => value != null ? value.toFixed(2) : '-',
    },
    {
      field: 'Blocks',
      headerName: 'Blocks',
      type: 'number',
      flex: 1,
      minWidth: 80,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Blocks
        </Typography>
      ),
      valueFormatter: (value) => value != null ? value.toFixed(2) : '-',
    },
  ];

  // NFL-specific stat columns
  const nflStatColumns = [
    {
      field: 'PassingYards',
      headerName: 'Pass Yds',
      type: 'number',
      flex: 1,
      minWidth: 100,
      renderHeader: () => <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>Pass Yds</Typography>,
      valueFormatter: (value) => (value != null ? value.toFixed(1) : '-'),
    },
    {
      field: 'PassAttempts',
      headerName: 'Pass Att',
      type: 'number',
      flex: 1,
      minWidth: 90,
      renderHeader: () => <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>Pass Att</Typography>,
      valueFormatter: (value) => (value != null ? value.toFixed(2) : '-'),
    },
    {
      field: 'Completions',
      headerName: 'Pass Comp',
      type: 'number',
      flex: 1,
      minWidth: 90,
      renderHeader: () => <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>Pass Comp</Typography>,
      valueFormatter: (value) => (value != null ? value.toFixed(2) : '-'),
    },
    {
      field: 'PassingTD',
      headerName: 'Pass TD',
      type: 'number',
      flex: 1,
      minWidth: 90,
      renderHeader: () => <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>Pass TD</Typography>,
      valueFormatter: (value) => (value != null ? value.toFixed(2) : '-'),
    },
    {
      field: 'Interceptions',
      headerName: 'INT',
      type: 'number',
      flex: 0.8,
      minWidth: 70,
      renderHeader: () => <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>INT</Typography>,
      valueFormatter: (value) => (value != null ? value.toFixed(2) : '-'),
    },
    {
      field: 'RushAttempts',
      headerName: 'Rush Att',
      type: 'number',
      flex: 1,
      minWidth: 90,
      renderHeader: () => <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>Rush Att</Typography>,
      valueFormatter: (value) => (value != null ? value.toFixed(2) : '-'),
    },
    {
      field: 'RushingYards',
      headerName: 'Rush Yds',
      type: 'number',
      flex: 1,
      minWidth: 100,
      renderHeader: () => <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>Rush Yds</Typography>,
      valueFormatter: (value) => (value != null ? value.toFixed(1) : '-'),
    },
    {
      field: 'Receptions',
      headerName: 'Rec',
      type: 'number',
      flex: 0.8,
      minWidth: 70,
      renderHeader: () => <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>Rec</Typography>,
      valueFormatter: (value) => (value != null ? value.toFixed(2) : '-'),
    },
    {
      field: 'ReceivingYards',
      headerName: 'Rec Yds',
      type: 'number',
      flex: 1,
      minWidth: 90,
      renderHeader: () => <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>Rec Yds</Typography>,
      valueFormatter: (value) => (value != null ? value.toFixed(1) : '-'),
    },
    {
      field: 'Touchdowns',
      headerName: 'Total TD',
      type: 'number',
      flex: 0.8,
      minWidth: 80,
      renderHeader: () => <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>Total TD</Typography>,
      valueFormatter: (value) => (value != null ? value.toFixed(2) : '-'),
    },
    {
      field: 'halfPPR',
      headerName: '0.5 PPR',
      type: 'number',
      flex: 1,
      minWidth: 90,
      renderHeader: () => <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>0.5 PPR</Typography>,
      valueFormatter: (value) => (value != null ? value.toFixed(2) : '-'),
    },
    {
      field: 'fullPPR',
      headerName: 'PPR',
      type: 'number',
      flex: 0.8,
      minWidth: 80,
      renderHeader: () => <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>PPR</Typography>,
      valueFormatter: (value) => (value != null ? value.toFixed(2) : '-'),
    },
  ];

  // Choose columns based on selected league's sport type
  const columns = useMemo(() => {
    if (isBasketball) {
      return [...sharedColumns, ...nbaStatColumns];
    }
    return [...sharedColumns, ...nflStatColumns];
  }, [isBasketball, sharedColumns]);

  // Process projections with filters
  const processedProjections = useMemo(() => {
    if (!Array.isArray(activeProjections)) return [];

    let filtered = activeProjections;

    // Football: only fantasy-relevant positions (props exist for others —
    // e.g. DL sacks/tackles — but they don't belong on this page)
    if (!isBasketball) {
      const FOOTBALL_POSITIONS = new Set(['QB', 'RB', 'WR', 'TE']);
      filtered = filtered.filter(proj => FOOTBALL_POSITIONS.has(proj?.Position));
    }

    // Filter by position - intelligent filtering for basketball
    if (selectedPosition !== 'All') {
      if (isBasketball) {
        filtered = filtered.filter(proj => {
          const position = proj?.Position || '';
          // Check if the position string contains the selected position letter
          // e.g., "G-F" contains both "G" and "F"
          return position.includes(selectedPosition);
        });
      } else { // Football - exact position match
        filtered = filtered.filter(proj => proj?.Position === selectedPosition);
      }
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(proj => {
        const name = (proj?.PlayerName || '').toLowerCase();
        const event = `${teamMap[proj?.awayTeamId]?.abbreviation || ''} @ ${teamMap[proj?.homeTeamId]?.abbreviation || ''}`.toLowerCase();
        return name.includes(query) || event.includes(query);
      });
    }

    // Deduplicate by _key (player + fixture)
    const seen = new Set();
    filtered = filtered.filter(proj => {
      const key = proj._key || `${proj.PersonId}-${proj.fixture_id}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    // Add stable row IDs + fantasy point projections (football).
    // Standard scoring: pass yds /25, pass TD 4, INT -2, rush/rec yds /10,
    // non-passing TD 6, receptions 0.5 (half) / 1.0 (full).
    // A projection is only shown when every stat that materially drives the
    // position's score is present — incomplete data shows blank instead of a
    // misleading partial number.
    const PPR_REQUIRED = {
      QB: ['PassingYards', 'PassingTD', 'Interceptions', 'RushingYards', 'Touchdowns'],
      RB: ['RushingYards', 'ReceivingYards', 'Receptions', 'Touchdowns'],
      WR: ['ReceivingYards', 'Receptions', 'Touchdowns'],
      TE: ['ReceivingYards', 'Receptions', 'Touchdowns'],
    };
    const ppr = (p, recPts) => {
      const required = PPR_REQUIRED[p.Position];
      if (!required) return null; // K/DEF/IDP etc.: no meaningful market coverage
      if (required.some(f => p[f] == null)) return null;
      return (p.PassingYards || 0) / 25 + (p.PassingTD || 0) * 4 - (p.Interceptions || 0) * 2
        + (p.RushingYards || 0) / 10 + (p.ReceivingYards || 0) / 10
        + (p.Touchdowns || 0) * 6 + (p.Receptions || 0) * recPts;
    };
    return filtered.map((proj, index) => ({
      ...proj,
      id: proj._key || proj.PersonId || `proj-${index}`,
      halfPPR: isBasketball ? undefined : ppr(proj, 0.5),
      fullPPR: isBasketball ? undefined : ppr(proj, 1.0),
    }));
  }, [activeProjections, isBasketball, selectedPosition, searchQuery, teamMap]);

  // Export to CSV
  const exportToCSV = useCallback(() => {
    if (!isAuthenticated) {
      alert('Please log in to export data.');
      return;
    }

    if (processedProjections.length === 0) {
      alert('No data to export');
      return;
    }

    const leagueName = selectedLeague || 'Projections';

    // Build a plain-text version of the Event column (team abbreviations + start time)
    // since the grid's Event column renders JSX and has no scalar value on the row.
    const formatEvent = (proj) => {
      const away = teamMap[proj?.awayTeamId]?.abbreviation || '';
      const home = teamMap[proj?.homeTeamId]?.abbreviation || '';
      const matchup = away && home ? `${away}@${home}` : (away || home || '');
      const start = proj?.EventStart
        ? new Date(proj.EventStart).toLocaleString('en-US', {
            month: 'numeric', day: 'numeric', year: '2-digit',
            hour: 'numeric', minute: '2-digit', hour12: true,
          }).replace(',', '')
        : '';
      return [matchup, start].filter(Boolean).join(' ');
    };

    const headers = columns
      .filter(col => col.field !== 'id')
      .map(col => col.headerName || col.field);

    const rows = processedProjections.map(proj =>
      columns
        .filter(col => col.field !== 'id')
        .map(col => {
          const value = col.field === 'Event' ? formatEvent(proj) : proj[col.field];
          if (value == null || value === '') return '';
          if (typeof value === 'number') return Math.round(value * 100) / 100; // strip float artifacts for clean Excel import
          return `"${String(value).replace(/"/g, '""')}"`;
        })
    );

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `${leagueName}_projections_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [processedProjections, columns, isAuthenticated, selectedLeague, teamMap]);

  // Copy to Clipboard
  const copyToClipboard = useCallback(() => {
    if (!isAuthenticated) {
      alert('Please log in to copy data.');
      return;
    }

    if (processedProjections.length === 0) {
      alert('No data to copy');
      return;
    }

    const formatEvent = (proj) => {
      const away = teamMap[proj?.awayTeamId]?.abbreviation || '';
      const home = teamMap[proj?.homeTeamId]?.abbreviation || '';
      const matchup = away && home ? `${away}@${home}` : (away || home || '');
      const start = proj?.EventStart
        ? new Date(proj.EventStart).toLocaleString('en-US', {
            month: 'numeric', day: 'numeric', year: '2-digit',
            hour: 'numeric', minute: '2-digit', hour12: true,
          }).replace(',', '')
        : '';
      return [matchup, start].filter(Boolean).join(' ');
    };

    const headers = columns
      .filter(col => col.field !== 'id')
      .map(col => col.headerName || col.field);

    const rows = processedProjections.map(proj =>
      columns
        .filter(col => col.field !== 'id')
        .map(col => {
          const value = col.field === 'Event' ? formatEvent(proj) : proj[col.field];
          if (value == null || value === '') return '';
          if (typeof value === 'number') return Math.round(value * 100) / 100; // strip float artifacts for clean Excel import
          return String(value);
        })
    );

    const tsvContent = [
      headers.join('\t'),
      ...rows.map(row => row.join('\t'))
    ].join('\n');

    // Fallback method for embedded contexts where navigator.clipboard may not work
    const fallbackCopy = (text) => {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        document.execCommand('copy');
      } catch (err) {
        console.error('Fallback copy failed:', err);
        alert('Failed to copy to clipboard');
      }

      document.body.removeChild(textArea);
    };

    // Try modern clipboard API first, fall back to execCommand
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(tsvContent).catch(() => {
        fallbackCopy(tsvContent);
      });
    } else {
      fallbackCopy(tsvContent);
    }
  }, [processedProjections, columns, isAuthenticated, teamMap]);

  return (
    <ThemeProvider theme={customTheme}>
      <StyledEngineProvider injectFirst>
        <CssBaseline />
        <Box sx={{
          width: '100%',
          height: '100vh',
          backgroundColor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          <ProjectionsTutorial />
          {/* Filter Section */}
          <Box className="filter-section" sx={{
            padding: '16px',
            backgroundColor: '#121212',
            borderBottom: '1px solid #333'
          }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ flexWrap: 'wrap', rowGap: 1, minWidth: 0, flex: 1 }}>
                {/* League Dropdown */}
                <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }} data-tutorial="league-filter">
                  <InputLabel id="league-filter-label" shrink sx={{ color: '#ffffff' }}>
                    League
                  </InputLabel>
                  <Select
                    labelId="league-filter-label"
                    label="League"
                    notched
                    value={selectedLeague}
                    onChange={(e) => {
                      setSelectedLeague(e.target.value);
                      setCurrentPage(0);
                    }}
                  >
                    {leagueOptions.map(league => (
                      <MenuItem key={league} value={league}>{league}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Position Tabs */}
                <Tabs
                  value={positionTabs.indexOf(selectedPosition)}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{
                    minHeight: 38,
                    maxWidth: '100%',
                    minWidth: 0,
                    flexShrink: 1,
                    '& .MuiTabs-scrollButtons': { color: '#ffffff', width: 24 },
                    '& .MuiTabs-scrollButtons.Mui-disabled': { opacity: 0.3 },
                    '& .MuiTab-root': {
                      color: '#ffffff',
                      minWidth: 48,
                      minHeight: 32,
                      padding: '4px 10px',
                      '&.Mui-selected': { color: '#ff1744' }
                    },
                    '& .MuiTabs-indicator': { backgroundColor: '#ff1744' }
                  }}
                >
                  {positionTabs.map((position) => (
                    <Tab key={position} label={position} />
                  ))}
                </Tabs>

                {/* Search Bar */}
                <TextField
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search player or matchup"
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#BBBBBB' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: 225,
                    '& .MuiInputBase-input': {
                      color: '#ffffff'
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#BBBBBB',
                      opacity: 1
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.875rem'
                    },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#ffffff',
                      },
                      '&:hover fieldset': {
                        borderColor: '#ff1744',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#ff1744',
                      },
                    },
                  }}
                />
              </Stack>

              <Stack direction="row" spacing={0.5} data-tutorial="projections-actions">
                <Tooltip
                  title={
                    !isAuthenticated
                      ? "Please log in to enable copy"
                      : processedProjections.length === 0
                      ? "No data to copy"
                      : "Copy to clipboard"
                  }
                  arrow
                >
                  <span>
                    <IconButton
                      onClick={copyToClipboard}
                      disabled={processedProjections.length === 0 || !isAuthenticated}
                      sx={{
                        color: '#bbbbbb',
                        padding: '4px',
                        '&:hover': { backgroundColor: 'rgba(187, 187, 187, 0.1)' },
                        '&.Mui-disabled': { color: '#666666' },
                      }}
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip
                  title={
                    !isAuthenticated
                      ? "Please log in to enable exports"
                      : processedProjections.length === 0
                      ? "No data to export"
                      : "Export to CSV"
                  }
                  arrow
                >
                  <span>
                    <IconButton
                      onClick={exportToCSV}
                      disabled={processedProjections.length === 0 || !isAuthenticated}
                      sx={{
                        color: '#bbbbbb',
                        padding: '4px',
                        '&:hover': { backgroundColor: 'rgba(187, 187, 187, 0.1)' },
                        '&.Mui-disabled': { color: '#666666' },
                      }}
                    >
                      <DownloadIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>
          </Box>

          {/* Data Grid */}
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <Box className="table-section" sx={{ height: '100%' }}>
              <Box className="data-grid-container" sx={{ height: '100%', boxSizing: 'border-box' }}>
                <DataGridPro
                  rows={processedProjections}
                  columns={columns}
                  pageSize={pageSize}
                  onPageSizeChange={setPageSize}
                  rowsPerPageOptions={[50, 100, 250]}
                  pagination
                  paginationMode="client"
                  page={currentPage}
                  onPageChange={setCurrentPage}
                  sortingMode="client"
                  sortModel={sortModel}
                  onSortModelChange={setSortModel}
                  disableSelectionOnClick
                  disableColumnFilter
                  disableColumnMenu
                  loading={!activeProjections || activeProjections.length === 0}
                  getRowId={(row) => row.id}
                  hideFooterSelectedRowCount
                  sx={{
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: '#1e1e1e',
                      borderBottom: '2px solid #444',
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                    },
                    '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
                    '& .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      color: '#ffffff !important',
                      userSelect: 'none',
                    },
                    '& .MuiDataGrid-cell': {
                      color: '#ffffff !important',
                      fontSize: '0.875rem',
                      userSelect: 'none',
                      borderRight: '1px solid rgba(255, 255, 255, 0.3)',
                    },
                    '& .MuiDataGrid-cell:last-child': {
                      borderRight: 'none',
                    },
                    '& .MuiDataGrid-cell:focus': { outline: 'none' },
                    '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
                    '& .MuiDataGrid-row:hover': { backgroundColor: '#1a1a1a' },
                    '& .MuiDataGrid-row.Mui-selected': {
                      backgroundColor: '#1a1a1a',
                      '&:hover': { backgroundColor: '#1a1a1a' },
                    },
                    '& .MuiDataGrid-columnSeparator': {
                      visibility: 'visible',
                      color: 'rgba(255, 255, 255, 0.3)',
                    },
                    '& .MuiDataGrid-sortIcon': { color: '#ffffff !important' },
                    '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      color: '#ffffff !important',
                    },
                    '& .MuiSelect-icon': { color: '#ffffff !important' },
                    '& .MuiDataGrid-menuIconButton': { color: '#ffffff !important' },
                    '& .MuiDataGrid-root': {
                      overflowY: 'auto !important',
                      margin: 0,
                      boxSizing: 'border-box',
                    },
                    '& .MuiDataGrid-virtualScroller': {
                      overflowY: 'auto !important',
                    },
                    '& .MuiDataGrid-main': {
                      padding: 0,
                      boxSizing: 'border-box',
                    },
                    '& .MuiDataGrid-footerContainer': {
                      backgroundColor: '#1e1e1e',
                      color: '#ffffff !important',
                      position: 'sticky',
                      bottom: 0,
                      zIndex: 1,
                      boxSizing: 'border-box',
                    },
                  }}
                  rowBuffer={10}
                  rowThreshold={5}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Game Log Modal */}
        <GameLogModal
          open={gameLogModalOpen}
          onClose={handleCloseModal}
          playerData={selectedPlayerData}
          gameLogData={gameLogData}
          loading={gameLogLoading}
          sport={selectedLeague}
        />
      </StyledEngineProvider>
    </ThemeProvider>
  );
};

export default Projections;